import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import client, { resolveApiAssetUrl } from '../../lib/api';
import { useGameLauncher } from '../../hooks/useGameLauncher';
import { useSettings } from '../../context/SettingsContext';
import { useVisibleHomeCategories } from '../../lib/homeCategories';
import { cacheSliders, readCachedSliders } from '../../lib/sliderCache';
import { slidersAt } from '../../lib/sliderPlacement';
import useSwipe from '../../hooks/useSwipe';
import GameModal from '../../components/GameModal';
import GameChoiceModal from '../../components/GameChoiceModal';
import AuthModal from '../../components/AuthModal';
import ProviderStrip, { providersFromResponse } from '../../components/ProviderStrip';
import MeritSection from './MeritSection';
import MeritGameCard from './MeritGameCard';
import { SECTIONS, HOOKS, hook, sectionHook } from '../../lib/domHooks';
import HomeBlocks, { HomeBlock } from '../../components/HomeBlocks';

const STRIP_LIMIT = 14;
const FEATURE_LIMIT = 7;
// Hero'da bannerlar tam genişlikte, tek tek sayfalanıp döner.
const HERO_PER_PAGE = 1;
const HERO_INTERVAL = 6000;

const mapGame = (game) => ({
  id: game.id,
  name: game.name,
  provider: game.provider,
  img: game.image,
  hot: game.hot,
  new: game.new,
  api_provider: game.api_provider,
  game_id: game.game_id,
  demo_supported: !!game.demo_supported,
});

const MeritHome = () => {
  const settings = useSettings();
  const gl = useGameLauncher();

  // Klasik vitrinle aynı önbellek: yanıt beklenirken son bannerlar çizilir,
  // taze liste gelince üzerine yazılır (bkz. lib/sliderCache.js).
  const [banners, setBanners] = useState(readCachedSliders);
  const [featured, setFeatured] = useState([]);
  const [popular, setPopular] = useState([]);
  const [live, setLive] = useState([]);
  const [fresh, setFresh] = useState([]);
  const [crash, setCrash] = useState([]);
  const [providers, setProviders] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [heroPage, setHeroPage] = useState(0);
  const visibleCategories = useVisibleHomeCategories(settings.sports_enabled);

  useEffect(() => {
    const get = (url, params) => client.get(url, { params }).then(({ data }) => data).catch(() => null);

    // Ağ hatasında (`data === null`) önbellekten çizilen bannerlar korunur.
    get('/sliders').then((data) => {
      if (!data) return;
      cacheSliders(data.items || []);
      setBanners(data.items || []);
    });
    // Vitrin şeritleri panelden seçilir (Oyun Yönetimi → Ana Sayfa Vitrini);
    // hiç oyun işaretlenmemişse uç otomatik kaynakla doldurur, şerit boş kalmaz.
    get('/games/showcase', { strip: 'featured', category: 'slot', limit: FEATURE_LIMIT })
      .then((data) => setFeatured(data?.items || []));
    // Üstteki şeritle örtüşenler ayıklanacağı için biraz fazla istenir.
    get('/games/showcase', { strip: 'popular', category: 'slot', limit: STRIP_LIMIT + FEATURE_LIMIT })
      .then((data) => setPopular(data?.items || []));
    get('/games/showcase', { strip: 'new', category: 'slot', limit: STRIP_LIMIT })
      .then((data) => setFresh(data?.items || []));
    get('/games', { category: 'live-casino', limit: STRIP_LIMIT }).then((data) => setLive(data?.items || []));
    get('/games', { category: 'crash', limit: STRIP_LIMIT }).then((data) => setCrash(data?.items || []));
    get('/game-providers', { category: 'slot' }).then((data) => {
      setProviders(providersFromResponse(data));
    });
    get('/bonuses').then((data) => setBonuses((data?.items || data || []).slice(0, 5)));
  }, []);

  // Merit hero'su yalnızca görseli çizer (metin/CTA alanı yok); görselsiz
  // banner burada boş bir kutu olurdu, bu yüzden elenir.
  // Yalnızca ana slider banner'ları: 'side' ve katalog sayfası banner'ları
  // buraya düşmemelidir (bkz. lib/sliderPlacement.js).
  const visibleBanners = slidersAt(banners).filter(
    (banner) => (
      banner.image
      && (banner.cta_link !== '/spor' || settings.sports_enabled !== false)
    ),
  );
  // Bannerlar tek tek sayfalanır; paneldeki tüm görünür bannerlar sırayla döner.
  const heroPageCount = Math.max(1, Math.ceil(visibleBanners.length / HERO_PER_PAGE));
  const activeHeroPage = heroPage % heroPageCount;
  const heroBanners = visibleBanners.slice(
    activeHeroPage * HERO_PER_PAGE,
    activeHeroPage * HERO_PER_PAGE + HERO_PER_PAGE,
  );

  // Sayaç heroPage'e de bağlıdır: parmakla kaydıran oyuncu seçtiği banner'ı
  // tam süre görsün, otomatik geçiş kaydırmanın hemen ardına düşmesin.
  useEffect(() => {
    if (heroPageCount < 2) return undefined;
    const timer = setInterval(
      () => setHeroPage((page) => (page + 1) % heroPageCount),
      HERO_INTERVAL,
    );
    return () => clearInterval(timer);
  }, [heroPageCount, heroPage]);

  // Mobilde banner parmakla çevrilir; Merit'te ok yoktur, tek kumanda noktalar
  // ve bu jesttir.
  const heroSwipe = useSwipe({
    enabled: heroPageCount > 1,
    onNext: () => setHeroPage((page) => (page + 1) % heroPageCount),
    onPrev: () => setHeroPage((page) => (page - 1 + heroPageCount) % heroPageCount),
  });

  // İki şerit de otomatik dolduğunda aynı oyunları göstermesin.
  const featuredIds = new Set(featured.map((game) => game.id));
  const popularStrip = popular
    .filter((game) => !featuredIds.has(game.id))
    .slice(0, STRIP_LIMIT);

  return (
    <>
      <HomeBlocks theme="merit">
        <HomeBlock id={SECTIONS.HERO}>
        {heroBanners.length > 0 && (
          <div className="merit-hero-wrap" {...heroSwipe} {...sectionHook(SECTIONS.HERO)}>
            <div
              key={activeHeroPage}
              className={`merit-hero ${heroBanners.length === 1 ? 'is-single' : ''}`}
                {...hook(HOOKS.SLIDER)}
            >
              {heroBanners.map((banner, index) => (
                <Link
                  key={banner.id || index}
                  to={banner.cta_link || '/promosyonlar'}
                  className="merit-hero-item"
                >
                  <img src={resolveApiAssetUrl(banner.image)} alt={banner.title || 'Kampanya'} />
                </Link>
              ))}
            </div>
            {heroPageCount > 1 && (
              <div className="merit-hero-dots">
                {Array.from({ length: heroPageCount }, (_, page) => (
                  <button
                    key={page}
                    type="button"
                    className={`merit-hero-dot ${page === activeHeroPage ? 'is-active' : ''}`}
                    onClick={() => setHeroPage(page)}
                    aria-label={`${page + 1}. banner`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.CATEGORIES}>
        {visibleCategories.length > 0 && (
          <div className="merit-tiles" {...sectionHook(SECTIONS.CATEGORIES)}>
            {visibleCategories.map((category) => (
              <Link
                key={category.id}
                to={category.href || '/'}
                className="merit-tile"
                {...hook(HOOKS.CATEGORY_CARD)}
              >
                <span className="merit-tile-copy">
                  <span className="merit-tile-title block">{category.title}</span>
                  <span className="merit-tile-sub block">{category.subtitle}</span>
                  <span className="merit-tile-cta">{category.cta_text}</span>
                </span>
                {category.image && (
                  <img
                    className="merit-tile-art"
                    src={resolveApiAssetUrl(category.image)}
                    alt=""
                    aria-hidden="true"
                  />
                )}
              </Link>
            ))}
          </div>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.PICKS}>
        {featured.length > 0 && (
          <MeritSection section={SECTIONS.PICKS} title="Size Özel Seçtiklerimiz">
            {featured.map((game) => (
              <MeritGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </MeritSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.POPULAR}>
        {popularStrip.length > 0 && (
          <MeritSection section={SECTIONS.POPULAR} title="Popüler Slotlar" to="/slotlar">
            {popularStrip.map((game) => (
              <MeritGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </MeritSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.LIVE_CASINO}>
        {live.length > 0 && (
          <MeritSection section={SECTIONS.LIVE_CASINO} title="Canlı Casino" to="/canli-casino">
            {live.map((game) => (
              <MeritGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </MeritSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.NEW}>
        {fresh.length > 0 && (
          <MeritSection section={SECTIONS.NEW} title="Yeni Eklenenler" to="/slotlar">
            {fresh.map((game) => (
              <MeritGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </MeritSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.FAST}>
        {crash.length > 0 && (
          <MeritSection section={SECTIONS.FAST} title="Hızlı Oyunlar" to="/crash">
            {crash.map((game) => (
              <MeritGameCard key={game.id} game={mapGame(game)} onPlay={gl.play} />
            ))}
          </MeritSection>
        )}
        </HomeBlock>

        <HomeBlock id={SECTIONS.PROVIDERS}>
        <ProviderStrip providers={providers} classPrefix="merit" />
        </HomeBlock>

        <HomeBlock id={SECTIONS.BONUSES}>
        {bonuses.length > 0 && (
          <MeritSection section={SECTIONS.BONUSES} title="Bonuslar" to="/promosyonlar" wide>
            {bonuses.map((bonus, index) => (
              <Link
                key={bonus.id || index}
                to="/promosyonlar"
                className="merit-hero-item"
                style={{ paddingTop: '56%' }}
              >
                {bonus.image ? (
                  <img src={resolveApiAssetUrl(bonus.image)} alt={bonus.title || 'Bonus'} />
                ) : (
                  <span
                    style={{
                      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: 6, padding: 16,
                      textAlign: 'center',
                    }}
                  >
                    <span style={{ fontSize: 22, fontWeight: 900, color: 'var(--pusu-gold)' }}>
                      {bonus.amount_text || bonus.title}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--pusu-muted)' }}>{bonus.subtitle || bonus.description}</span>
                  </span>
                )}
              </Link>
            ))}
          </MeritSection>
        )}
        </HomeBlock>
      </HomeBlocks>

      <GameChoiceModal
        game={gl.choice}
        user={gl.user}
        balance={gl.balance}
        bonusBalance={gl.bonusBalance}
        demoAvailable={gl.demoAvailable}
        onClose={gl.closeChoice}
        onReal={gl.startReal}
        onDemo={gl.startDemo}
        onLogin={() => gl.openAuth('login')}
        onRegister={() => gl.openAuth('register')}
      />
      {gl.activeGame && (
        <GameModal
          key={`${gl.activeGame.id}-${gl.mode}`}
          game={gl.activeGame}
          mode={gl.mode}
          onClose={gl.closeGame}
        />
      )}
      <AuthModal
        open={!!gl.authTab}
        onClose={() => gl.setAuthTab(null)}
        defaultTab={gl.authTab || 'login'}
      />
    </>
  );
};

export default MeritHome;
